import React, { useContext, useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import clsx from "clsx";
import { DateTime } from "luxon";
import { useHistory, useLocation } from "react-router-dom";
import mainStyles from "styles";

import { StartRetroShareResponse } from "@safetyculture/s12-apis-connect-web/s12/templates/admin/v1/templates_pb";
import {
  RetroShareProcess,
  RetroShareStatus,
} from "@safetyculture/s12-apis-connect-web/s12/templates/v1/template_retroshare_pb";
import { ListRetroShareResponse } from "@safetyculture/s12-apis-connect-web/s12/templates/v1/templates_pb";

import {
  listRetroshares,
  startRetroShare,
} from "api/endpoints/data-governance";
import { callOptionsHeaders } from "api/helpers";
import { regionValuesArr } from "api/index";
import DisplayError from "components/shared/display-error";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";
import { DEFAULT_REGION_CODE } from "constants/index";
import { RetroShareStatusMap, RetroShareStatuses } from "constants/retroshare";
import { SnackbarContext } from "context/snackbar-context";
import { convertTimestampToDate } from "helpers/time";
import {
  SearchRetroShareDocument,
  SearchRetroShareQuery,
  StartRetroShareDocument,
  StartRetroShareQuery,
} from "types/search-display";

type LocationState = {
  orgId: string;
  regionCode: string;
};

const RetroShares: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();

  const classes = mainStyles();

  const { orgId, regionCode } = location.state || {
    orgId: "",
    regionCode: DEFAULT_REGION_CODE,
  };
  const [dataCenterRegion, setDataCenterRegion] = useState<string>(regionCode);
  const [retroShareStarted, setRetroShareStarted] = useState(false);
  const [searchErrorValidationMessage, setSearchErrorValidationMessage] =
    useState("");
  const [startErrorValidationMessage, setStartErrorValidationMessage] =
    useState("");
  const { showError } = useContext(SnackbarContext);

  const [searchRetroShareQueryState, setSearchRetroShareQueryState] =
    useState<SearchRetroShareQuery>({
      orgId: orgId,
      templateId: "",
      userId: "",
      status: RetroShareStatus.UNSPECIFIED,
      cursor: "",
    });

  const [searchRetroShareDocument, setSearchRetroShareDocument] =
    useState<SearchRetroShareDocument>({
      retroShareResp: new ListRetroShareResponse({
        items: [],
        cursor: "",
      }),
      isLoading: false,
    });

  const [startRetroShareQueryState, setStartRetroShareQueryState] =
    useState<StartRetroShareQuery>({
      orgId: orgId,
      templateId: "",
    });

  const [startRetroShareDocument, setStartRetroShareDocument] =
    useState<StartRetroShareDocument>({
      retroShareResp: new StartRetroShareResponse(),
      isLoading: false,
    });

  const shouldRenderSeeMoreBtn =
    !searchRetroShareDocument.isLoading &&
    searchRetroShareDocument.retroShareResp.items.length !== 0;

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string
  ) => {
    setSearchRetroShareQueryState({
      ...searchRetroShareQueryState,
      [key]: event.target.value,
    });
  };

  const handleSearchStatusChange = (
    event: SelectChangeEvent<RetroShareStatus>
  ) => {
    setSearchRetroShareQueryState({
      ...searchRetroShareQueryState,
      status: event.target.value as RetroShareStatus,
    });
  };

  const handleStartChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string
  ) => {
    setStartRetroShareQueryState({
      ...startRetroShareQueryState,
      [key]: event.target.value,
    });
  };

  const handleDataCenterRegionChange = (event: SelectChangeEvent<string>) => {
    setDataCenterRegion(event.target.value);
  };

  const makeSearchReq = (showMore: boolean) => {
    let updatedSearchRetroShare = {
      ...searchRetroShareDocument,
    };
    updatedSearchRetroShare.isLoading = true;
    setSearchRetroShareDocument(updatedSearchRetroShare);

    listRetroshares(
      {
        orgId: searchRetroShareQueryState.orgId,
        templateId: searchRetroShareQueryState.templateId,
        userId: searchRetroShareQueryState.userId,
        status: searchRetroShareQueryState.status,
        cursor: searchRetroShareQueryState.cursor,
      },
      callOptionsHeaders(dataCenterRegion, orgId)
    )
      .then((searchRespObject) => {
        updatedSearchRetroShare = {
          ...searchRetroShareDocument,
        };
        updatedSearchRetroShare.isLoading = false;
        updatedSearchRetroShare.retroShareResp.cursor = searchRespObject.cursor;
        if (showMore) {
          updatedSearchRetroShare.retroShareResp.items =
            updatedSearchRetroShare.retroShareResp.items.concat(
              searchRespObject.items
            );
        } else {
          updatedSearchRetroShare.retroShareResp.items = searchRespObject.items;
        }

        setSearchRetroShareDocument(updatedSearchRetroShare);
      })
      .catch((error) => {
        updatedSearchRetroShare = {
          ...searchRetroShareDocument,
        };
        updatedSearchRetroShare.isLoading = false;
        setSearchRetroShareDocument(updatedSearchRetroShare);
        showError(error);
      });
  };

  const handleSearchRetroShare = () => {
    const updatedRetroShareQueryState = searchRetroShareQueryState;
    updatedRetroShareQueryState.cursor = "";
    setSearchRetroShareQueryState(updatedRetroShareQueryState);
    makeSearchReq(false);
  };

  const handleResultsPageUpdate = () => {
    const updatedRetroShareQueryState = searchRetroShareQueryState;
    updatedRetroShareQueryState.cursor =
      searchRetroShareDocument.retroShareResp.cursor;
    setSearchRetroShareQueryState(updatedRetroShareQueryState);
    makeSearchReq(true);
  };

  const validateTemplateId = (templateId: string) => {
    return /^template_[a-fA-F0-9]{32}$/.test(templateId);
  };

  const validateOrgId = (orgId: string) => {
    return /^role_[a-fA-F0-9]{32}$/.test(orgId);
  };

  const handleStartRetroShare = () => {
    if (
      !startRetroShareQueryState.orgId ||
      !validateOrgId(startRetroShareQueryState.orgId) ||
      !startRetroShareQueryState.templateId
    ) {
      setStartErrorValidationMessage(
        "A valid Template ID and Organisation ID are required to start Retroshare."
      );
      return;
    }

    const templatesIDs = startRetroShareQueryState.templateId.split(",");
    for (const template of templatesIDs) {
      if (!template || !validateTemplateId(template.trim())) {
        setStartErrorValidationMessage(
          `Templates Id '${template}' is invalid.`
        );
        return;
      }
    }

    let updatedStartRetroShare = {
      ...startRetroShareDocument,
    };
    updatedStartRetroShare.isLoading = true;
    setStartRetroShareDocument(updatedStartRetroShare);

    const organisationID = startRetroShareQueryState.orgId.split("_").pop();
    setRetroShareStarted(true);
    for (const template of templatesIDs) {
      const templateID = template.split("_").pop();
      if (templateID && organisationID) {
        startRetroShare(
          {
            orgId: organisationID,
            templateId: templateID,
          },
          callOptionsHeaders(dataCenterRegion, orgId)
        )
          .then((searchRespObject) => {
            updatedStartRetroShare = {
              ...startRetroShareDocument,
            };
            updatedStartRetroShare.isLoading = false;
            updatedStartRetroShare.retroShareResp = searchRespObject;

            setStartRetroShareDocument(updatedStartRetroShare);
            handleSearchRetroShare();
          })
          .catch((error) => {
            updatedStartRetroShare = {
              ...startRetroShareDocument,
            };
            updatedStartRetroShare.isLoading = false;
            setStartRetroShareDocument(updatedStartRetroShare);
            showError(error);
          });
      }
    }
  };

  // Reset error message display
  useEffect(() => {
    setSearchErrorValidationMessage("");
  }, [searchRetroShareQueryState]);

  useEffect(() => {
    setStartErrorValidationMessage("");
  }, [startRetroShareQueryState]);

  const clearSearchInputs = () => {
    setSearchRetroShareQueryState({
      ...searchRetroShareQueryState,
      orgId: "",
      templateId: "",
      userId: "",
      status: RetroShareStatus.UNSPECIFIED,
      cursor: "",
    });
  };

  const clearSearchForm = () => {
    setSearchErrorValidationMessage("");
    clearSearchInputs();
  };

  const clearSearchResults = () => {
    setSearchRetroShareDocument(searchRetroShareDocument);
  };

  const clearStartInputs = () => {
    setStartRetroShareQueryState({
      ...startRetroShareQueryState,
      orgId: "",
      templateId: "",
    });
  };

  const clearStartForm = () => {
    setStartErrorValidationMessage("");
    clearStartInputs();
  };

  const clearStartResults = () => {
    setStartRetroShareDocument(startRetroShareDocument);
  };

  const pageUrl = history.location.pathname;

  history.listen((location, action) => {
    if (location.pathname === pageUrl && action !== "POP") {
      // Didn't press back button
      clearSearchInputs();
      clearSearchResults();
      clearStartInputs();
      clearStartResults();
    }
  });

  const populateWithRetroShares = () => {
    if (searchRetroShareDocument.retroShareResp.items.length == 0) {
      return (
        <TableRow>
          <TableCell colSpan={7}>
            <b>{"No results found."}</b>
          </TableCell>
        </TableRow>
      );
    }

    return searchRetroShareDocument.retroShareResp.items.map(
      (row: RetroShareProcess, index: number) => (
        <TableRow key={`${index}-${row.id}`}>
          <TableCell>{row.templateId}</TableCell>
          <TableCell>{row.id}</TableCell>
          <TableCell>{RetroShareStatusMap.get(row.status)}</TableCell>
          <TableCell>
            {String(row.complete)} /{" "}
            {String(row.complete + row.failed + row.skipped + row.pending)}
          </TableCell>
          <TableCell>{row.userId}</TableCell>
          <TableCell>{row.organisationId}</TableCell>
          <TableCell>
            {row.createdAt
              ? DateTime.fromJSDate(
                  convertTimestampToDate(row.createdAt)
                ).toISO()
              : ""}
          </TableCell>
        </TableRow>
      )
    );
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Box
          component="form"
          sx={classes.spaceSearch}
          noValidate
          autoComplete="off"
        >
          <TextField
            style={{ width: 350 }}
            sx={{ m: 1 }}
            id="start-retro-template-id"
            data-testid="start-retro-template-id"
            label="Template ID"
            value={startRetroShareQueryState.templateId}
            onChange={(event) => handleStartChange(event, "templateId")}
            variant="outlined"
            size="small"
            inputProps={{
              "data-anchor": "start-retro-template-id-input",
              "data-testid": "start-retro-template-id-input",
            }}
            onKeyUp={(event) => {
              if (event.key === "Enter") {
                handleStartRetroShare();
              }
            }}
            required
          />
          <TextField
            style={{ width: 350 }}
            sx={{ m: 1 }}
            id="start-retro-org-id"
            data-testid="start-retro-org-id"
            label="Organisation ID"
            value={startRetroShareQueryState.orgId}
            onChange={(event) => handleStartChange(event, "orgId")}
            variant="outlined"
            size="small"
            inputProps={{
              "data-anchor": "start-retro-org-id-input",
              "data-testid": "start-retro-org-id-input",
            }}
            onKeyUp={(event) => {
              if (event.key === "Enter") {
                handleStartRetroShare();
              }
            }}
            required
          />
          <FormControl
            variant="outlined"
            size="small"
            sx={{ ...classes.formControlRetro, m: 1 }}
          >
            <InputLabel id="select-region-label-start-retro">
              Region/Data Center
            </InputLabel>
            <Select
              labelId="start-retro-region-label"
              id="start-retro-region-id"
              value={dataCenterRegion}
              label="Region/Data Center"
              onChange={handleDataCenterRegionChange}
              data-testid="start-retro-region"
              disabled={false}
            >
              {regionValuesArr.map((region, index) => (
                <MenuItem
                  key={`${region}_${index}`}
                  value={region}
                  data-testid={"region-code-" + region + "-option"}
                >
                  {region.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            disabled={false}
            variant="contained"
            color="primary"
            onClick={handleStartRetroShare}
            data-anchor="start-retroshare-btn"
            data-testid="start-retroshare-btn"
            sx={{ m: 1 }}
          >
            Start Retroshare
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={clearStartForm}
            data-anchor="start-retro-clear-btn"
            data-testid="start-retro-clear-btn"
            sx={{ m: 1 }}
          >
            Clear
          </Button>
        </Box>

        <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          open={retroShareStarted}
          autoHideDuration={DEFAULT_SNACKBAR_DURATION}
          onClose={() => setRetroShareStarted(false)}
          data-testid="retroshare-started-success"
        >
          <Alert severity="success" variant="filled">
            Retroshare(s) started.
          </Alert>
        </Snackbar>
        <DisplayError errorMessage={startErrorValidationMessage} />
      </Grid>
      <Grid item xs={12}>
        <form
          className={clsx(classes.spaceSearchRetro)}
          noValidate
          autoComplete="off"
        >
          <TextField
            style={{ width: 300 }}
            sx={{ m: 1 }}
            id="searc-retro-template-id"
            data-testid="search-retro-template-id"
            label="Template ID"
            value={searchRetroShareQueryState.templateId}
            onChange={(event) => handleSearchChange(event, "templateId")}
            variant="outlined"
            size="small"
            inputProps={{
              "data-anchor": "search-retro-template-id-input",
              "data-testid": "search-retro-template-id-input",
            }}
            onKeyUp={(event) => {
              if (event.key === "Enter") {
                handleSearchRetroShare();
              }
            }}
            required
          />
          <TextField
            style={{ width: 300 }}
            sx={{ m: 1 }}
            id="search-retro-org-id"
            data-testid="search-retro-org-id"
            label="Organisation ID"
            value={searchRetroShareQueryState.orgId}
            onChange={(event) => handleSearchChange(event, "orgId")}
            variant="outlined"
            size="small"
            inputProps={{
              "data-anchor": "search-retro-org-id-input",
              "data-testid": "search-retro-org-id-input",
            }}
            onKeyUp={(event) => {
              if (event.key === "Enter") {
                handleSearchRetroShare();
              }
            }}
            required
          />
          <TextField
            style={{ width: 300 }}
            sx={{ m: 1 }}
            id="search-retro-user-id"
            data-testid="search-retro-user-id"
            label="User ID"
            value={searchRetroShareQueryState.userId}
            onChange={(event) => handleSearchChange(event, "userId")}
            variant="outlined"
            size="small"
            inputProps={{
              "data-anchor": "search-retro-user-id-input",
            }}
            onKeyUp={(event) => {
              if (event.key === "Enter") {
                handleSearchRetroShare();
              }
            }}
          />
          <FormControl
            variant="outlined"
            size="small"
            sx={{ ...classes.formControlRetro, m: 1 }}
          >
            <InputLabel id="select-doc-type-label">
              RetroShare Status
            </InputLabel>
            <Select
              labelId="search-select-doc-type-label"
              id="search-select-doc-type"
              value={searchRetroShareQueryState.status}
              label="RetroShare Status"
              onChange={handleSearchStatusChange}
              data-anchor="search-retro-status-select"
              data-testid="search-retro-status-select"
              disabled={false}
            >
              {RetroShareStatuses.map((status) => (
                <MenuItem
                  key={status.id}
                  value={status.value}
                  data-anchor={"doc-type-" + status.id + "-option"}
                  data-testid={"doc-type-" + status.id + "-option"}
                >
                  {status.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            variant="outlined"
            size="small"
            sx={{ ...classes.formControlRetro, m: 1 }}
          >
            <InputLabel id="select-region-label">Region/Data Center</InputLabel>
            <Select
              labelId="search-region-label"
              id="search-region"
              value={dataCenterRegion}
              label="Region/Data Center"
              onChange={handleDataCenterRegionChange}
              data-testid="search-retro-region"
              disabled={false}
            >
              {regionValuesArr.map((region, index) => (
                <MenuItem
                  key={`${region}_${index}`}
                  value={region}
                  data-testid={"region-code-" + region + "-option"}
                >
                  {region.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            disabled={false}
            variant="contained"
            color="primary"
            onClick={handleSearchRetroShare}
            data-anchor="search-retro-search-btn"
            data-testid="search-retro-search-btn"
            sx={{ m: 1 }}
          >
            Search
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={clearSearchForm}
            data-anchor="search-retro-clear-btn"
            data-testid="search-retro-clear-btn"
            sx={{ m: 1 }}
          >
            Clear
          </Button>
        </form>
        <DisplayError errorMessage={searchErrorValidationMessage} />
      </Grid>
      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table aria-label="user-list-table">
            <TableHead>
              <TableRow>
                <TableCell>Template ID</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Inspections</TableCell>
                <TableCell>User ID</TableCell>
                <TableCell>Organisation ID</TableCell>
                <TableCell>Started</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {searchRetroShareDocument.isLoading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : (
                populateWithRetroShares()
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell
                  colSpan={7}
                  sx={{
                    ...classes.paddingTopSmall,
                    ...classes.paddingBottomSmall,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="flex-end"
                  >
                    <Box>
                      {shouldRenderSeeMoreBtn && (
                        <Button
                          disabled={false}
                          color="primary"
                          onClick={handleResultsPageUpdate}
                          data-anchor="retro-show-more-btn"
                          data-testid="retro-show-more-btn"
                        >
                          ...SEE MORE
                        </Button>
                      )}
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default RetroShares;
